import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { heroData } from '../../data/mock';
import DemoModal from './DemoModal';

const WATERMARK_LOGO = 'https://customer-assets.emergentagent.com/job_coin-control-1/artifacts/2m2ji0tz_85e9384d-89d9-4ef4-b04a-b727bf317f00.jpg';

const HeroSection = () => {
  const heroRef = useRef(null);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      heroRef.current.style.setProperty('--mouse-x', `${x}%`);
      heroRef.current.style.setProperty('--mouse-y', `${y}%`);
    };

    const hero = heroRef.current;
    if (hero) {
      hero.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (hero) {
        hero.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen bg-black overflow-hidden"
      style={{ '--mouse-x': '80%', '--mouse-y': '20%' }}
    >
      {/* Background Crypto Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1622630998477-20aa696ecb05?q=80&w=2946&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-[0.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black" />
      </div>
      
      {/* Ambient Light Effect */}
      <div 
        className="absolute inset-0 opacity-60 transition-all duration-1000"
        style={{
          background: `radial-gradient(ellipse 80% 50% at var(--mouse-x) var(--mouse-y), rgba(255,200,100,0.15) 0%, rgba(255,150,50,0.08) 25%, transparent 60%)`,
        }}
      />
      
      {/* Secondary Glow */}
      <div className="absolute top-0 right-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] opacity-40">
        <div className="absolute inset-0 bg-gradient-radial from-amber-500/20 via-orange-500/5 to-transparent blur-3xl animate-pulse-slow" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '40px 40px md:60px 60px'
      }} />

      {/* Animated Logo Watermark + Scroll Parallax */}
      <div
        className="absolute inset-0 z-0 flex items-center justify-center overflow-hidden pointer-events-none"
        aria-hidden="true"
        data-testid="hero-watermark"
      >
        <div
          className="relative"
          style={{
            transform: `translateY(${scrollY * 0.28}px) scale(${1 + scrollY * 0.00035})`,
            opacity: Math.max(0, 1 - scrollY / 650),
            transition: 'transform 0.1s linear',
          }}
        >
          {/* Rotating ring glow */}
          <div className="hero-watermark-ring absolute inset-0 -m-16 md:-m-24 rounded-full border border-amber-500/10" />
          <div className="hero-watermark-ring absolute inset-0 -m-28 md:-m-40 rounded-full border border-amber-500/[0.06]" style={{ animationDirection: 'reverse' }} />
          <img
            src={WATERMARK_LOGO}
            alt=""
            className="hero-watermark w-[280px] md:w-[560px] lg:w-[720px] opacity-[0.07] select-none mix-blend-screen"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col min-h-screen">
        <div className="flex-1 flex items-center">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16 w-full pt-20">
            {/* Large Brand Typography - Animated */}
            <div className="mb-6 md:mb-10 animate-fade-in">
              <div className="relative block group">
                {/* Glow Effect Behind Text */}
                <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-amber-500/20 via-amber-400/10 to-transparent transition-all duration-700 group-hover:from-amber-500/30 group-hover:via-amber-400/20" />
                <h1 className="brand-text-animated relative text-[clamp(3rem,14vw,10rem)] leading-[0.85] select-none block transition-all duration-500 hover:scale-[1.02]">
                  COIN
                </h1>
              </div>
              <div className="relative block group">
                <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-amber-400/10 via-amber-500/20 to-transparent transition-all duration-700 group-hover:from-amber-400/20 group-hover:via-amber-500/30" />
                <h1 className="brand-text-animated relative text-[clamp(3rem,14vw,10rem)] leading-[0.85] select-none block transition-all duration-500 hover:scale-[1.02]" style={{ animationDelay: '0.5s' }}>
                  BILLS
                </h1>
              </div>
            </div>
            
            {/* Tagline */}
            <div className="max-w-3xl animate-fade-in-up animation-delay-200">
              <p className="text-amber-500/70 text-[10px] md:text-xs font-medium tracking-[0.2em] md:tracking-[0.3em] mb-2 md:mb-4">
                {heroData.tagline.toUpperCase()}
              </p>
              <h2 className="text-lg md:text-2xl lg:text-4xl font-light text-white leading-[1.3] tracking-tight mb-3 md:mb-6">
                {heroData.headline}
              </h2>
              <p className="text-white/50 text-xs md:text-base lg:text-lg font-light leading-relaxed max-w-xl">
                {heroData.subtext}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 md:gap-4 mt-6 md:mt-10 animate-fade-in-up animation-delay-400">
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className="group px-5 md:px-8 py-2.5 md:py-3.5 text-[10px] md:text-xs font-medium tracking-[0.15em] md:tracking-[0.2em] bg-white text-black hover:bg-white/90 transition-all duration-500 rounded-sm flex items-center gap-2 md:gap-3"
              >
                GET LIVE DEMO
                <ArrowRight className="w-3 h-3 md:w-4 md:h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button 
                onClick={() => scrollToSection('#about')}
                className="px-5 md:px-8 py-2.5 md:py-3.5 text-[10px] md:text-xs font-medium tracking-[0.15em] md:tracking-[0.2em] border border-white/20 text-white/80 hover:border-white/40 transition-all duration-500 rounded-sm"
              >
                LEARN MORE
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="pb-6 md:pb-10 flex justify-center animate-bounce-slow">
          <button 
            onClick={() => scrollToSection('#about')}
            className="text-white/30 hover:text-white/60 transition-colors duration-300"
          >
            <ChevronDown className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
      </div>

      {/* Demo Modal */}
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />
    </section>
  );
};

export default HeroSection;
